// El desglose opcional de una entrada del cuaderno.
//
// Funciones puras, sin React ni imports, para poder ejecutarlas con node y
// probarlas — el mismo trato que timelineScale.js y cronologiaDia.js.
//
// Una subtarea es { id, texto, hecha, minutos }. Los minutos son INFORMATIVOS:
// el tiempo de la entrada lo sigue poniendo la persona, y lo unico que puede
// cambiarlo es el boton de ajustar. Aqui no se calcula nada solo.

export const TOPE_SUBTAREAS = 50;
export const TOPE_TEXTO     = 200;
export const MAX_MINUTOS    = 1440;

// Id de cliente. Es obligatorio y no es un capricho: con la posicion como key,
// React reutiliza la fila al reordenar o borrar, y el resultado es que se
// pierde el foco al teclear y que una casilla parece marcarse sola.
export const nuevoId = () => Math.random().toString(36).slice(2, 10);

// null = sin estimar, que no es lo mismo que cero minutos.
const aMinutos = v => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.min(MAX_MINUTOS, Math.round(n)) : null;
};

export const nuevaSubtarea = (texto = "", minutos = null) => ({
  id: nuevoId(),
  texto: String(texto).slice(0, TOPE_TEXTO),
  hecha: false,
  minutos: aMinutos(minutos),
});

// Espejo en cliente del saneado del servidor. Lo que llega del GET ya viene
// limpio, pero una entrada guardada antes de este cambio trae `undefined` y una
// tocada a mano puede traer cualquier cosa.
export function normaliza(v) {
  if (!Array.isArray(v)) return [];
  return v.slice(0, TOPE_SUBTAREAS).map(s => ({
    id: (typeof s?.id === "string" && s.id) || nuevoId(),
    texto: String(s?.texto ?? "").slice(0, TOPE_TEXTO),
    hecha: !!s?.hecha,
    minutos: aMinutos(s?.minutos),
  }));
}

// Todas devuelven un array nuevo: el estado de la entrada se reemplaza entero
// con {...a, ...patch}, asi que mutar el que hay dentro no se veria.
const mapa = (items, id, fn) =>
  normaliza(items).map(s => (String(s.id) === String(id) ? fn(s) : s));

export const add       = (items, texto = "", minutos = null) =>
  [...normaliza(items), nuevaSubtarea(texto, minutos)];
export const quitar    = (items, id)   => normaliza(items).filter(s => String(s.id) !== String(id));
export const toggle    = (items, id)   => mapa(items, id, s => ({ ...s, hecha: !s.hecha }));
export const setTexto  = (items, id, t) =>
  mapa(items, id, s => ({ ...s, texto: String(t).slice(0, TOPE_TEXTO) }));
export const setMinutos = (items, id, m) => mapa(items, id, s => ({ ...s, minutos: aMinutos(m) }));

// dir: -1 sube, +1 baja. En los extremos no pasa nada, que es lo que espera
// quien pulsa la flecha de subir en la primera fila.
export function mover(items, id, dir) {
  const lista = normaliza(items);
  const i = lista.findIndex(s => String(s.id) === String(id));
  const j = i + (dir < 0 ? -1 : 1);
  if (i < 0 || j < 0 || j >= lista.length) return lista;
  const copia = [...lista];
  [copia[i], copia[j]] = [copia[j], copia[i]];
  return copia;
}

// Fuente unica de los contadores: la pildora del Registro, el aviso de
// descuadre y el contador de la Cronologia salen todos de aqui.
export function resumenSubtareas(items) {
  const lista = normaliza(items);
  return {
    n: lista.length,
    hechas: lista.filter(s => s.hecha).length,
    minutos: lista.reduce((sum, s) => sum + (s.minutos || 0), 0),
    sinMinutos: lista.filter(s => !s.minutos).length,
  };
}

// Cuadra o no cuadra la suma de las subtareas con el tiempo de la entrada.
//
// Con la suma a cero el estado es "vacio" y no se avisa de nada: quien no pone
// duraciones no quiere un aviso recordandoselo en cada entrada.
export function descuadre(items, minutosEntrada) {
  const { minutos: suma, sinMinutos } = resumenSubtareas(items);
  const total = Math.max(0, Math.round(Number(minutosEntrada) || 0));
  const diff = suma - total;
  const estado = suma === 0 ? "vacio" : diff === 0 ? "cuadra" : diff < 0 ? "faltan" : "sobran";
  return { suma, total, diff, estado, incompleto: sinMinutos > 0 };
}
